import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactionsPopup from "./ReactionsPopup";
import {
  getCurrentUserReaction,
  getReactionSummary,
  getTotalReactionCount,
  formatReactionCount,
  getReactionEmoji,
} from "../../helpers/reactionsHelper";
import {
  useReactToPost,
  useReactToComment,
} from "../../Controllers/Tribe/TribePostController";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

/**
 * ReactionButton Component
 * Handles reactions for both posts and comments
 * Shows current reaction and popup on hover
 */
const ReactionButton = ({
  type = "post", // 'post' or 'comment'
  targetId, // post.id or comment.id
  reactionsData = null, // Reactions data from API response
  availableReactions = [], // Available reaction types
  onReactionUpdate, // Callback when reaction is updated
  formatLikes, // Format function for displaying counts
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const buttonRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const queryClient = useQueryClient();

  const { mutateAsync: reactToPost } = useReactToPost();
  const { mutateAsync: reactToComment } = useReactToComment();

  // Get current user reaction - recalculate on every render
  const currentUserReaction = useMemo(() => {
    return reactionsData ? getCurrentUserReaction(reactionsData) : null;
  }, [reactionsData]);

  // Get reaction summary
  const reactionSummary = useMemo(() => {
    return reactionsData ? getReactionSummary(reactionsData) : [];
  }, [reactionsData]);

  // Calculate total count
  const totalCount = useMemo(() => {
    return (
      reactionsData?.total_count ||
      getTotalReactionCount(reactionSummary, reactionsData)
    );
  }, [reactionsData, reactionSummary]);

  // Get emoji for current reaction - handle both reaction_type_id and id
  const currentReactionEmoji = useMemo(() => {
    if (!currentUserReaction || !availableReactions.length) return null;
    const reactionId =
      currentUserReaction.reaction_type_id || currentUserReaction.id;
    return reactionId ? getReactionEmoji(reactionId, availableReactions) : null;
  }, [currentUserReaction, availableReactions]);

  const hideTimeoutRef = useRef(null);

  // Handle mouse enter - show popup after short delay
  const handleMouseEnter = () => {
    setIsHovering(true);
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setShowPopup(true);
    }, 300); // 300ms delay before showing popup
  };

  // Handle mouse leave - hide popup
  const handleMouseLeave = () => {
    setIsHovering(false);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Delay hiding popup to allow moving to popup
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = setTimeout(() => {
      setShowPopup(false);
    }, 200);
  };

  // Handle popup mouse enter - keep it open
  const handlePopupMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  // Handle popup mouse leave - close it
  const handlePopupMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setShowPopup(false);
    }, 200);
  };

  // Handle reaction selection
  const handleReactionSelect = async (reaction) => {
    try {
      const selectedReactionId = reaction.id;

      let response;
      if (type === "post") {
        response = await reactToPost({
          postId: targetId,
          reactionId: selectedReactionId,
        });
      } else {
        response = await reactToComment({
          commentId: targetId,
          reactionId: selectedReactionId,
        });
      }

      // Transform API response to match our expected format
      const apiData = response?.data?.data || response?.data;

      if (onReactionUpdate && apiData) {
        const transformedData = {
          current_user_reaction: apiData.user_reaction
            ? {
                reaction_type_id: apiData.user_reaction.id,
                emoji: apiData.user_reaction.emoji,
                name: apiData.user_reaction.name,
              }
            : null,
          summary: apiData.reactions
            ? apiData.reactions.map((r) => ({
                reaction_type_id: r.reaction_type?.id || r.reaction_type_id,
                emoji: r.reaction_type?.emoji || r.emoji,
                name: r.reaction_type?.name || r.name,
                count: r.count || 0,
              }))
            : [],
          total_count: apiData.reactions
            ? apiData.reactions.reduce((total, r) => total + (r.count || 0), 0)
            : 0,
        };
        onReactionUpdate(transformedData);
      }

      // Invalidate queries to refetch updated data
      if (type === "post") {
        queryClient.invalidateQueries({
          queryKey: ["tribePostDetails", targetId],
        });
      }

      // Keep popup open briefly to show feedback, then close
      setTimeout(() => {
        setShowPopup(false);
      }, 300);
    } catch (error) {
      console.error("Error reacting:", error);
      toast.error("Failed to react. Please try again.");
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Determine which icon to show (only used when no emoji reaction)
  const getIconSrc = () => {
    // Always show unfilled like icon when no emoji reaction
    return "icons/post-like.svg";
  };

  return (
    <div
      ref={buttonRef}
      className="d-flex align-items-center gap-2"
      style={{
        cursor: "pointer",
        position: "relative", // needed to anchor popup
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Reaction Icon/Emoji + REACT text */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          position: "relative",
        }}
      >
        {/* Show emoji if user has reacted, otherwise show like icon */}
        {currentReactionEmoji ? (
          <span
            style={{
              fontSize: "20px",
              lineHeight: "1",
            }}
          >
            {currentReactionEmoji}
          </span>
        ) : (
          <img
            src={getIconSrc()}
            alt="like"
            style={{
              width: "24px",
              height: "24px",
            }}
          />
        )}
        {/* Reaction Count */}
        {totalCount > 0 && (
          <span style={{ color: "#D0D8DF" }} className="counts-text">
            {formatReactionCount(totalCount) || formatLikes?.(totalCount)}
          </span>
        )}

        {/* REACT text */}
        <span
          className="react-text"
          style={{
            fontSize: "14px",
            fontWeight: 400,
            color: "#D0D8DF",
          }}
        >
          React
        </span>
      </div>

      {/* Reactions Popup */}
      {showPopup && availableReactions.length > 0 && (
        <div
          onMouseEnter={handlePopupMouseEnter}
          onMouseLeave={handlePopupMouseLeave}
          style={{
            position: "absolute",
            bottom: "130%", // above the button
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 20,
          }}
        >
          <ReactionsPopup
            reactions={availableReactions}
            currentUserReaction={currentUserReaction}
            reactionSummary={reactionSummary}
            onReactionSelect={handleReactionSelect}
            onClose={() => setShowPopup(false)}
            position="top"
          />
        </div>
      )}
    </div>
  );
};

export default ReactionButton;
